/**
 * Typen und Hilfen der Seiten NEA und SIMARIS (J16, 13.10.2026; überarbeitet
 * ES, 16.10.2026).
 *
 * Die Typen folgen den JSON-Antworten von `GET …/elektro/nea` und
 * `GET …/elektro/simaris` (NeaEndpoints.cs, SimarisEndpoints.cs). Die Befunde
 * kommen als Kennwort; ihren Klartext und ihre Stufe liefert der Server im
 * `befundKatalog` (eine Stelle für Seite, PDF und Excel).
 */
import type { Zustand } from '../../muster';

/** PDF-Knöpfe beider Seiten: neue Berichtsfassung nur bei geändertem Stand, sonst die abgelegte (F11). */
export const PDF_HINWEIS =
  'Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit Dokumentnummer an (immer Vorabzug); '
  + 'sonst liefert er die abgelegte Fassung (F11). '
  + 'Die Excel-Tabelle ist ein Arbeitsstand und legt keine Fassung an.';

export interface OffeneFrage { nr: string; frage: string }

// ═══ NEA ═══════════════════════════════════════════════════════════════════

export interface NeaAbgang {
  reihenfolge: number; seite: number | null; ebene: string | number | null;
  bezeichnung: string | null; kennzeichen: string | null; elternkennzeichen: string | null;
  standort: string | null; menge: number | null; pQuelleKw: number | null;
  gleichzeitigkeit: number | null; ibA: number | null; sKva: number | null;
  inQuellsumme: boolean | null; modellVerteilerId: string | null;
  modellVersorgungsart: string | null; modellZentraleId: string | null; befund: string | null;
}

export interface NeaPruefzeile { gegenstand: string; soll: string; ist: string; bewertung: string; grundlage: string }

export interface NeaLaufzeit {
  bezug: string; lastKw: number; lastProzent: number | null; tankL: number | null;
  beDatenblattGkwh: number | null; beTankblattGkwh: number | null;
  verbrauchDatenblattLh: number | null; verbrauchTankblattLh: number | null;
  laufzeitDatenblattH: number | null; laufzeitTankblattH: number | null;
  betriebszeitTankblattH: number | null; hinweis: string | null;
}

export interface NeaPruefpunkt { nr: string; gegenstand: string; grundlage: string; stand: string; text: string }

export interface NeaAnlage {
  id: string; kennzeichen: string; bezeichnung: string;
  geschossQuelle: string | null; betriebsfall: string | null;
  datenblatt: Record<string, unknown>;
  tank: Record<string, unknown>;
  bilanz: Record<string, unknown>;
  herkunft: Record<string, unknown>;
  befunde: string[];
  generatorIk3dKa?: number | null;
  laufzeit?: NeaLaufzeit | null;
  generatorpruefung?: NeaPruefzeile[];
  wirkleistungKw?: number | null;
  abgaenge: NeaAbgang[];
}

export interface NeaAuslastung {
  variante: string; bezug: string; lastKw: number; lastKva: number; nichtSummiertKw: number;
  grenzeKw: number | null; grenzeKva: number | null; prozentKw: number | null;
  prozentKwMitNichtSummiert: number | null; prozentKwGegenUeberlast: number | null; prozentKva: number | null;
  ueberNennleistung: boolean; ueberUeberlast: boolean;
}

export interface NeaStand {
  anlagen: NeaAnlage[];
  varianten: Partial<NeaAuslastung>[];
  pruefpunkte?: NeaPruefpunkt[];
  offeneFragen: OffeneFrage[];
}

/** Die Befunde einer Anlage nach Thema: die Zeilen tragen „Tank: …“ und „Modell: …“ vorn. */
export function befundeGegliedert(befunde: string[]): { titel: string; zeilen: string[] }[] {
  const tank = befunde.filter((b) => b.startsWith('Tank: ')).map((b) => b.slice(6));
  const modell = befunde.filter((b) => b.startsWith('Modell: ')).map((b) => b.slice(8));
  const bilanz = befunde.filter((b) => !b.startsWith('Tank: ') && !b.startsWith('Modell: '));
  return [
    { titel: 'Bilanz', zeilen: bilanz },
    { titel: 'Tank und Verbrauch', zeilen: tank },
    { titel: 'Abgleich mit dem Modell', zeilen: modell },
  ].filter((g) => g.zeilen.length > 0);
}

/** Der Befund eines NEA-Abgangs im Klartext (wie NeaNachweisPdf.Befundtext). */
export function neaBefundText(befund: string | null): string {
  if (!befund) return '';
  const wort: Record<string, string> = {
    im_modell: 'im Modell', nicht_im_modell: 'nicht im Modell', modell_an_cps: 'an CPS',
    modell_ohne_bedarf: 'ohne Bedarf', nicht_summiert: 'nicht summiert',
  };
  return befund.split(',').map((b) => wort[b.trim()] ?? b.trim()).join(' · ');
}

/** Stand einer Prüfung als Marke. */
export function pruefZustand(stand: string): { zustand: Zustand; text: string } {
  switch (stand) {
    case 'erfuellt': return { zustand: 'gut', text: 'erfüllt' };
    case 'nicht_erfuellt': return { zustand: 'fehler', text: 'nicht erfüllt' };
    case 'offen': return { zustand: 'warnung', text: 'offen' };
    case 'daten_fehlen': return { zustand: 'neutral', text: 'Daten fehlen' };
    default: return { zustand: 'neutral', text: stand };
  }
}

// ═══ SIMARIS ═══════════════════════════════════════════════════════════════

export interface BefundEintrag { code: string; text: string; stufe: 'info' | 'hinweis' | 'warnung' | 'fehler' }

export interface SimarisEigen {
  ik3MaxKa: number | null; ik1MinKa: number | null; zsMOhm: number | null;
  herkunft: string | null; gerechnetAm: string | null; hinweis: string | null;
}

export interface SimarisKnoten {
  name: string; spannung: string | null; schluessel: string | null;
  modellVerteilerId: string | null; modellKennzeichen: string | null; zuordnung: string | null;
  ikMaxQuelleKa: number | null; ikGeraete?: string[]; ikModellKa: number | null; ikModellHerkunft?: string | null;
  ikAbweichungProzent: number | null;
  eigen?: SimarisEigen | null;
  zuleitung: string | null; zuleitungKabel?: string | null; zuleitungQuerschnittText: string | null;
  zuleitungQuerschnittQuelleMm2?: number | null; zuleitungAnzahlKabel?: number | null;
  zuleitungLaengeQuelleM?: number | null; zuleitungQuerschnittModellMm2: number | null;
  zuleitungLaengeModellM?: number | null; zuleitungModellHerkunft?: string | null;
  icwKa: number | null; icwModellKa: number | null; zuleitungsschutz?: string | null;
  selektivitaetIk3?: string | null; selektivitaetIk1Pe?: string | null; selektivitaetIk1N?: string | null;
  selektivitaetSeite?: number | null;
  befunde: string[];
}

export interface SimarisKabel {
  art: string; bezeichnung: string; typ: string | null; querschnittText: string | null; startZiel: string;
  material: string | null; laengeM: number | null; ibA: number | null; izA: number | null;
  verlegeart: string | null; fges: number | null; duProzent: number | null; summeDuProzent: number | null;
  anzahl: number | null; funktionserhalt: string | null; seite: number;
}

export interface SimarisUeberlast {
  kabel: string; art: string; startZiel: string; schutzgeraet: string | null; geraeteart: string | null;
  ausloeser: string | null; ibA: number | null; inA: number | null; izA: number | null; anzahl: number | null;
  bewertung: string; irVon: number | null; irBis: number | null; seite: number;
}

export interface SimarisFunktionserhalt {
  kabel: string; von: string; nach: string; typ: string | null; funktionserhalt: string | null;
  mitFunktionserhalt: boolean; bezug: string; seite: number;
}

export interface SimarisSelektivitaetGeraet {
  geraet: string; kreis: string; bestellnummer: string | null; ik3Ik2: string | null; ik1Pe: string | null;
  ik1N: string | null; gesamt: string | null; seiten: number[];
}

export interface SimarisBetriebsart { name: string; deaktiviert: boolean; verbindungen: number; ein: number }

export interface SimarisBaumzeile {
  ebene: number; knoten: string; verbindung: string | null; kabel: string | null;
  kabeltext: string | null; schutz: string | null; wiederholung: boolean;
}

export interface SimarisStand {
  lauf: Record<string, unknown> & { zaehlung?: Record<string, unknown>; dateien?: string[] };
  netzparameter: Record<string, unknown> | null;
  personenschutz: string | null;
  einspeisungen: { art: string; bezeichnung: string; ikMaxKa: number | null; ikMinKa: number | null; seite: number }[];
  betriebsarten?: SimarisBetriebsart[];
  netzbaum?: Record<string, SimarisBaumzeile[]>;
  ikMinZs: string;
  eigeneRechnungHinweis?: string | null;
  abgleich: Record<string, unknown> & { ueberlast?: Record<string, number> };
  knoten: SimarisKnoten[];
  kabel?: SimarisKabel[];
  spannungsfallUeberGrenze: Record<string, unknown>[];
  schaltvermoegenZuKlein?: Record<string, unknown>[];
  ueberlast?: SimarisUeberlast[];
  funktionserhalt?: SimarisFunktionserhalt[];
  selektivitaet: SimarisSelektivitaetGeraet[] | Record<string, unknown>[];
  modellOhneQuelle: { kennzeichen: string; art: string; versorgungsart: string }[] | Record<string, unknown>[];
  offeneFragen: OffeneFrage[];
  befundKatalog?: BefundEintrag[];
}

/** Stufe → Marke. „info“ zeigt keine Farbe. */
export function stufeZustand(stufe: string | undefined): Zustand {
  switch (stufe) {
    case 'fehler': return 'fehler';
    case 'warnung': return 'warnung';
    case 'hinweis': return 'info';
    default: return 'neutral';
  }
}

/** Der Katalog als Nachschlagetabelle; ohne Katalog bleibt das Kennwort stehen. */
export function befundLexikon(katalog: BefundEintrag[] | undefined): (code: string) => BefundEintrag {
  const je = new Map((katalog ?? []).map((e) => [e.code, e]));
  return (code) => je.get(code) ?? { code, text: code.replace(/_/g, ' '), stufe: 'hinweis' };
}

/** Die Befunde eines Knotens ohne „im Modell“ – das ist kein Befund. */
export function knotenBefunde(befunde: unknown): string[] {
  if (!Array.isArray(befunde)) return [];
  return befunde.filter((b): b is string => typeof b === 'string' && b !== 'im_modell');
}

/** Selektivitätsbewertung im Klartext. */
export function selektivText(b: string | null | undefined): string {
  if (!b) return '—';
  return b === 'nicht_ermittelbar' ? 'nicht ermittelbar' : b;
}

export function selektivZustand(b: string | null | undefined): Zustand {
  if (b === 'vollselektiv') return 'gut';
  if (b === 'teilselektiv') return 'warnung';
  return 'neutral';
}

// ═══ Anzeige ═══════════════════════════════════════════════════════════════

/** Zahl deutsch mit fester Nachkommazahl; leer als „—“. */
export function zahl(v: unknown, nachkomma = 1, einheit?: string): string {
  if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
  const t = v.toLocaleString('de-DE', { minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma });
  return einheit ? `${t} ${einheit}` : t;
}

/** Datum und Uhrzeit deutsch (UTC-Angabe aus dem Server). */
export function zeitpunkt(v: unknown): string {
  if (typeof v !== 'string' || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Ein Datum ohne Uhrzeit ("2026-06-29") deutsch; anderes bleibt, leer als „—“. */
export function datum(v: unknown): string {
  if (typeof v !== 'string' || v === '') return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

/** Wert für die Anzeige: Zahlen deutsch, Wahrheitswerte ja/nein, leer als „—“. */
export function anzeigeWert(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (Array.isArray(v)) return v.map(anzeigeWert).join(', ');
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>)
    .map(([k, w]) => `${k}: ${anzeigeWert(w)}`).join(' · ');
  return String(v);
}
